import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PaperAirplaneIcon,
  TrashIcon,
  QuestionMarkCircleIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { marked } from 'marked';
import apiClient, { exportAPI } from '../services/api';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { useActivitiesStore } from '../store/activitiesStore';

// Helper function to generate UUIDs with fallback
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

interface FinancialCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
}

interface AdditionalReadingLink {
  title: string;
  url: string;
  source: string;
  description?: string;
}

interface QuestionResult {
  session_id: string;
  category: string;
  question: string;
  answer: string;
  key_points: string[];
  action_items: string[];
  related_topics: string[];
  disclaimers: string[];
  follow_up_suggestions: string[];
  additional_reading: AdditionalReadingLink[];
  timestamp: Date;
}

// Default categories for financial questions
const defaultCategories: FinancialCategory[] = [
  { id: 'general', name: 'General Finance', description: 'General financial questions and concepts', icon: 'currency' },
  { id: 'investing', name: 'Investing', description: 'Stocks, bonds, mutual funds, ETFs, and investment strategies', icon: 'chart' },
  { id: 'retirement', name: 'Retirement', description: '401(k), IRA, pension, and retirement planning', icon: 'calendar' },
  { id: 'taxes', name: 'Taxes', description: 'Tax planning, deductions, credits, and filing', icon: 'receipt' },
  { id: 'credit', name: 'Credit & Debt', description: 'Credit scores, loans, mortgages, and debt management', icon: 'credit-card' },
  { id: 'budgeting', name: 'Budgeting', description: 'Personal budgeting, saving, and expense tracking', icon: 'wallet' },
  { id: 'insurance', name: 'Insurance', description: 'Life, health, auto, home, and other insurance', icon: 'shield' },
  { id: 'estate', name: 'Estate Planning', description: 'Wills, trusts, inheritance, and estate taxes', icon: 'document' },
  { id: 'realestate', name: 'Real Estate', description: 'Home buying, mortgages, and property investment', icon: 'home' },
  { id: 'education', name: 'Education Funding', description: '529 plans, student loans, and education savings', icon: 'academic' },
];

// Popular topics by category
const TOPICS_BY_CATEGORY: Record<string, string[]> = {
  general: [
    'What is compound interest and how does it work?',
    'How do I create an emergency fund?',
    'What is the difference between saving and investing?',
    'How much should I have in savings?',
    'What is inflation and how does it affect my money?',
  ],
  investing: [
    'How should I start investing as a beginner?',
    'What is the difference between stocks and bonds?',
    'What are ETFs and how do they work?',
    'How do I diversify my investment portfolio?',
    'What is dollar-cost averaging?',
  ],
  retirement: [
    'What is the difference between a 401(k) and IRA?',
    'How much should I save for retirement?',
    'What is a Roth IRA and who should use it?',
    'When can I withdraw from my retirement accounts?',
    'What is employer matching and how does it work?',
  ],
  taxes: [
    'What tax deductions am I eligible for?',
    'How do tax brackets work?',
    'Should I itemize or take the standard deduction?',
    'What is the difference between tax credits and deductions?',
    'How can I reduce my taxable income?',
  ],
  credit: [
    'How can I improve my credit score?',
    'What factors affect my credit score?',
    'How do I check my credit report for free?',
    'What is credit utilization and why does it matter?',
    'How long do negative items stay on my credit report?',
  ],
  budgeting: [
    'How do I create a monthly budget?',
    'What is the 50/30/20 budgeting rule?',
    'How can I track my expenses effectively?',
    'What are common budgeting mistakes to avoid?',
    'How much should I allocate for different expense categories?',
  ],
  insurance: [
    'What types of insurance do I really need?',
    'How much life insurance coverage should I have?',
    'What is the difference between term and whole life insurance?',
    'How do I choose the right health insurance plan?',
    'What factors affect my auto insurance rates?',
  ],
  estate: [
    'Do I need a will or a trust?',
    'What is probate and how can I avoid it?',
    'How do I set up beneficiaries for my accounts?',
    'What is a power of attorney?',
    'How does estate tax work?',
  ],
  realestate: [
    'How much house can I afford?',
    'What is the difference between pre-qualification and pre-approval?',
    'Should I rent or buy a home?',
    'What are closing costs when buying a home?',
    'How does a 15-year mortgage compare to a 30-year?',
  ],
  education: [
    'What is a 529 plan and how does it work?',
    'Should I pay off student loans or invest?',
    'What are the best ways to save for college?',
    'How do I apply for financial aid?',
    'What is the difference between subsidized and unsubsidized loans?',
  ],
  debt: [
    'What is the best strategy to pay off debt?',
    'Should I use the debt snowball or avalanche method?',
    'How do I consolidate my debt?',
    'Is it better to pay off debt or save?',
    'How can I negotiate with creditors?',
  ],
  housing: [
    'How much should I spend on housing?',
    'What are the hidden costs of homeownership?',
    'How do I know if I\'m ready to buy a house?',
    'What is PMI and how can I avoid it?',
    'Should I refinance my mortgage?',
  ],
  other: [
    'How do I start building wealth?',
    'What financial goals should I prioritize?',
    'How can I increase my income?',
    'What are the basics of financial literacy?',
    'How do I protect myself from financial fraud?',
  ],
};

// Helper function to get popular topics based on category
const getPopularTopics = (category: string): string[] => {
  return TOPICS_BY_CATEGORY[category] || TOPICS_BY_CATEGORY['general'];
};

export const FinancialQuestions: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  // Form state
  const [category, setCategory] = useState('general');
  const [question, setQuestion] = useState('');
  const [simpleMode, setSimpleMode] = useState(true);

  // UI state
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<FinancialCategory[]>(defaultCategories);
  const [questionHistory, setQuestionHistory] = useState<QuestionResult[]>([]);
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [initialQueryProcessed, setInitialQueryProcessed] = useState(false);

  // Activity management from store
  const { addActivity, updateActivity, currentActivityId, setCurrentActivityId, getActivityById } = useActivitiesStore();
  const [activityName, setActivityName] = useState('');
  const [lastSavedHistoryLength, setLastSavedHistoryLength] = useState(0);
  const [isSaved, setIsSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Handle initial query from navigation
  useEffect(() => {
    const state = location.state as { initialQuery?: string } | null;
    if (state?.initialQuery && !initialQueryProcessed) {
      setQuestion(state.initialQuery);
      setInitialQueryProcessed(true);
    }
  }, [location.state, initialQueryProcessed]);

  // Clear current activity and reset form when navigating without a specific activity to load
  useEffect(() => {
    const navState = location.state as { loadActivityId?: string } | null;
    if (!navState?.loadActivityId) {
      resetForm();
    }
  }, [location.pathname, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load activity if navigated from sidebar
  useEffect(() => {
    const navState = location.state as { loadActivityId?: string } | null;
    if (navState?.loadActivityId) {
      const activity = getActivityById(navState.loadActivityId);
      if (activity && activity.activityType === 'financial-questions') {
        if (hasUnsavedChanges) {
          const confirmed = window.confirm(
            'You have unsaved changes. Do you want to discard them and load the selected activity?'
          );
          if (!confirmed) return;
        }

        setActivityName(activity.name);
        const savedHistory = activity.data?.questionHistory || [];
        const convertedHistory = savedHistory.map((item: any) => ({
          ...item,
          timestamp: item.timestamp ? new Date(item.timestamp) : new Date(),
        }));
        setQuestionHistory(convertedHistory);
        if (convertedHistory.length > 0) {
          setActiveSessionId(convertedHistory[convertedHistory.length - 1].session_id);
        }
        setCurrentActivityId(activity.id);
        setLastSavedHistoryLength(savedHistory.length);
        setIsSaved(true);
        setHasUnsavedChanges(false);
      }
    }
  }, [location.state, getActivityById, hasUnsavedChanges, setCurrentActivityId]);

  // Track changes for unsaved state
  useEffect(() => {
    const historyChanged = questionHistory.length !== lastSavedHistoryLength;
    if (historyChanged) {
      setHasUnsavedChanges(true);
      setIsSaved(false);
    }
  }, [questionHistory.length, lastSavedHistoryLength]);

  // Load categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Scroll to top of results when new question is added
  useEffect(() => {
    if (questionHistory.length > 0) {
      resultsContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [questionHistory.length]);

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get('/financial-questions/categories');
      if (response.data.categories) {
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Failed to fetch categories, using defaults:', error);
      // Keep using default categories
    }
  };

  const resetForm = () => {
    setCurrentActivityId(null);
    setActivityName('');
    setCategory('general');
    setQuestion('');
    setQuestionHistory([]);
    setActiveSessionId(null);
    setLastSavedHistoryLength(0);
    setIsSaved(false);
    setHasUnsavedChanges(false);
    setError(null);
  };

  const handleAskQuestion = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!question.trim()) {
      setError(t('financialQuestions:errors.questionRequired', 'Please enter a question'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/financial-questions/ask', {
        category,
        question,
        simple_mode: simpleMode,
      });

      const data = response.data;

      const result: QuestionResult = {
        session_id: data.session_id || `session-${Date.now()}`,
        category,
        question,
        answer: data.answer,
        key_points: data.key_points || [],
        action_items: data.action_items || [],
        related_topics: data.related_topics || [],
        disclaimers: data.disclaimers || ['This information is for educational purposes only and should not be considered financial advice. Please consult a qualified financial advisor for personalized guidance.'],
        follow_up_suggestions: data.follow_up_suggestions || [],
        additional_reading: data.additional_reading || [],
        timestamp: new Date(),
      };

      const updatedHistory = [...questionHistory, result];
      setQuestionHistory(updatedHistory);
      setActiveSessionId(result.session_id);
      setQuestion('');

      // Auto-save the activity
      let generatedName = activityName;
      if (!generatedName.trim()) {
        try {
          const titleResponse = await apiClient.post('/financial-questions/generate-title', {
            question,
          });
          generatedName = titleResponse.data.title || question.substring(0, 50);
        } catch {
          generatedName = question.substring(0, 50);
        }
        setActivityName(generatedName);
      }

      const activityId = currentActivityId || generateUUID();
      if (currentActivityId) {
        updateActivity(currentActivityId, {
          name: generatedName,
          savedAt: new Date(),
          data: { questionHistory: updatedHistory },
        });
      } else {
        addActivity({
          id: activityId,
          name: generatedName,
          activityType: 'financial-questions',
          savedAt: new Date(),
          data: { questionHistory: updatedHistory },
        });
        setCurrentActivityId(activityId);
      }
      setLastSavedHistoryLength(updatedHistory.length);
      setIsSaved(true);
      setHasUnsavedChanges(false);

    } catch (error) {
      console.error('Question error:', error);
      setError(t('financialQuestions:errors.askFailed', 'Failed to get answer. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleFollowUp = async (sessionId: string) => {
    if (!followUpQuestion.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const lastQuestion = questionHistory.find(q => q.session_id === sessionId);

      const response = await apiClient.post('/financial-questions/follow-up', {
        session_id: sessionId,
        question: followUpQuestion,
        context: lastQuestion?.answer.substring(0, 2000) || '',
      });

      const data = response.data;

      const result: QuestionResult = {
        session_id: sessionId,
        category: lastQuestion?.category || 'follow-up',
        question: `Follow-up: ${followUpQuestion}`,
        answer: data.response || data.answer,
        key_points: [],
        action_items: [],
        related_topics: [],
        disclaimers: [data.disclaimer || 'This information is for educational purposes only.'],
        follow_up_suggestions: [],
        additional_reading: [],
        timestamp: new Date(),
      };

      const updatedHistory = [...questionHistory, result];
      setQuestionHistory(updatedHistory);
      setFollowUpQuestion('');

      // Auto-save the activity
      if (currentActivityId) {
        updateActivity(currentActivityId, {
          savedAt: new Date(),
          data: { questionHistory: updatedHistory },
        });
        setLastSavedHistoryLength(updatedHistory.length);
        setIsSaved(true);
        setHasUnsavedChanges(false);
      }

    } catch (error) {
      console.error('Follow-up error:', error);
      setError(t('financialQuestions:errors.followUpFailed', 'Failed to get follow-up response.'));
    } finally {
      setLoading(false);
    }
  };

  const deleteQuestion = (index: number) => {
    setQuestionHistory(questionHistory.filter((_, i) => i !== index));
  };

  // Generate export content for a question
  const generateExportContent = (result: QuestionResult): string => {
    const categoryName = categories.find(c => c.id === result.category)?.name || result.category;
    let content = `Financial Question\n`;
    content += `Date: ${result.timestamp.toLocaleString()}\n`;
    content += `Category: ${categoryName}\n\n`;
    content += `### Question\n${result.question}\n\n`;
    content += `${result.answer}\n\n`;

    if (result.key_points.length > 0) {
      content += `### Key Points\n`;
      result.key_points.forEach(p => content += `- ${p}\n`);
      content += '\n';
    }

    if (result.action_items.length > 0) {
      content += `### Suggested Actions\n`;
      result.action_items.forEach(a => content += `- ${a}\n`);
      content += '\n';
    }

    content += `### Disclaimer\n`;
    result.disclaimers.forEach(d => content += `${d}\n`);

    return content;
  };

  // Handle export for a specific result
  const handleExportForResult = (index: number) => async (format: 'word' | 'pdf' | 'text' | 'csv') => {
    if (format === 'csv') return;
    const result = questionHistory[index];
    if (!result) return;
    await exportDocument(result, format);
  };

  // Handle print for a specific result
  const handlePrintForResult = (index: number) => () => {
    const result = questionHistory[index];
    if (!result) return;
    handlePrint(result);
  };

  // Handle print
  const handlePrint = (result: QuestionResult) => {
    const categoryName = categories.find(c => c.id === result.category)?.name || result.category;
    // Convert markdown to HTML using marked
    const answerHtml = marked(result.answer, { async: false }) as string;
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${t('financialQuestions:title', 'Financial Question')}</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            max-width: 800px;
            margin: 0 auto;
            padding: 40px 20px;
            color: #333;
          }
          h1 { text-align: center; color: #d97706; border-bottom: 2px solid #d97706; padding-bottom: 10px; }
          h2 { color: #b45309; margin-top: 24px; }
          h3 { color: #92400e; margin-top: 16px; }
          .meta { text-align: center; color: #666; margin-bottom: 24px; }
          ul, ol { margin: 8px 0; padding-left: 24px; }
          li { margin-bottom: 6px; }
          p { margin: 8px 0; }
          strong { font-weight: 600; }
          em { font-style: italic; }
          code { background: #f3f4f6; padding: 2px 6px; border-radius: 4px; font-family: monospace; }
          pre { background: #f3f4f6; padding: 12px; border-radius: 8px; overflow-x: auto; }
          pre code { background: none; padding: 0; }
          blockquote { border-left: 4px solid #d97706; margin: 16px 0; padding-left: 16px; color: #666; }
          .answer-content h3 { font-size: 1.1em; margin-top: 20px; margin-bottom: 8px; }
          .answer-content ul, .answer-content ol { margin: 12px 0; }
          .answer-content li { margin-bottom: 8px; }
          .disclaimer { margin-top: 24px; padding: 12px; background: #fef3c7; border-radius: 8px; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>${t('financialQuestions:title', 'Financial Question')}</h1>
        <div class="meta">
          <p>${t('financialQuestions:date', 'Date')}: ${result.timestamp.toLocaleString()}</p>
          <p>${t('financialQuestions:category', 'Category')}: ${categoryName}</p>
        </div>
        <h2>${t('financialQuestions:yourQuestion', 'Your Question')}</h2>
        <p>${result.question}</p>
        <div class="answer-content">${answerHtml}</div>
        ${
          result.key_points.length > 0
            ? `<h2>${t('financialQuestions:keyPoints', 'Key Points')}</h2>
          <ul>${result.key_points.map((p) => `<li>${p}</li>`).join('')}</ul>`
            : ''
        }
        ${
          result.action_items.length > 0
            ? `<h2>${t('financialQuestions:actionItems', 'Suggested Actions')}</h2>
          <ul>${result.action_items.map((a) => `<li>${a}</li>`).join('')}</ul>`
            : ''
        }
        <div class="disclaimer">
          <strong>${t('financialQuestions:disclaimer.title', 'Disclaimer')}:</strong> ${result.disclaimers[0]}
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  // Export document to Word, PDF, or Text
  const exportDocument = async (result: QuestionResult, format: 'word' | 'pdf' | 'text') => {
    try {
      const content = generateExportContent(result);
      const title = t('financialQuestions:title', 'Financial Question');
      const extensionMap: Record<string, string> = { word: 'docx', pdf: 'pdf', text: 'txt' };
      const extension = extensionMap[format];
      const filename = `financial-question-${result.session_id.substring(0, 8)}.${extension}`;

      const blob = await exportAPI.exportDocument({
        content,
        title,
        format: extension as 'docx' | 'pdf' | 'txt',
      });

      // Try File System Access API
      if ('showSaveFilePicker' in window) {
        try {
          const mimeTypes: Record<string, string> = {
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            pdf: 'application/pdf',
            txt: 'text/plain',
          };

          const descriptions: Record<string, string> = {
            word: 'Word Document',
            pdf: 'PDF Document',
            text: 'Text File',
          };

          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [
              {
                description: descriptions[format],
                accept: { [mimeTypes[extension]]: [`.${extension}`] },
              },
            ],
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch (err: any) {
          if (err.name === 'AbortError') return;
        }
      }

      // Fallback download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      setError(t('financialQuestions:errors.exportFailed', 'Failed to export document'));
    }
  };

  return (
    <div className="flex flex-col h-full p-6">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center mb-4">
          <QuestionMarkCircleIcon className="h-12 w-12 text-amber-500" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {t('financialQuestions:title', 'Ask Financial Questions')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('financialQuestions:subtitle', 'Get AI-powered answers to your financial questions')}
        </p>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row gap-6 max-w-7xl mx-auto w-full overflow-hidden">
        {/* Left Panel - Options */}
        <div className="lg:w-1/4 flex flex-col space-y-4 overflow-y-auto">
          {/* Category Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('financialQuestions:form.category', 'Category')}
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            {category && categories.find(c => c.id === category) && (
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                {categories.find(c => c.id === category)?.description}
              </p>
            )}
          </div>

          {/* Simple Mode Toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('financialQuestions:form.simpleMode', 'Simple Mode')}
              </span>
              <p className="text-xs text-gray-600 dark:text-gray-300">
                {t('financialQuestions:form.simpleModeDesc', 'Get easy-to-understand explanations')}
              </p>
            </div>
            <button
              onClick={() => setSimpleMode(!simpleMode)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                simpleMode ? 'bg-amber-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  simpleMode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Popular Topics */}
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
            <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">
              {t('financialQuestions:popularTopics', 'Popular Topics')}
            </h3>
            <div className="space-y-1">
              {getPopularTopics(category).map((topic, i) => (
                <button
                  key={i}
                  onClick={() => setQuestion(topic)}
                  className="block w-full text-left text-sm text-amber-700 dark:text-amber-300 hover:underline"
                >
                  {topic}
                </button>
              ))}
            </div>
          </div>

          {/* Reset Button */}
          <button
            onClick={resetForm}
            className="w-full px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            {t('financialQuestions:reset', 'Start New Session')}
          </button>
        </div>

        {/* Right Panel - Question & Answers */}
        <div className="lg:w-3/4 flex flex-col bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden">
          {/* Question Input */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <form onSubmit={handleAskQuestion}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('financialQuestions:form.question', 'Your Question')}
              </label>
              <div className="relative">
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder={t('financialQuestions:form.questionPlaceholder', 'Ask any financial question...')}
                  rows={3}
                  className="w-full px-4 py-3 pr-12 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-500 resize-none"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || !question.trim()}
                  className="absolute bottom-3 right-3 p-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  title={t('financialQuestions:form.ask', 'Ask')}
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                </button>
              </div>
              {error && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
              )}
            </form>
          </div>

          {/* Question History */}
          <div ref={resultsContainerRef} className="flex-1 overflow-y-auto p-4 space-y-6">
            {questionHistory.length === 0 ? (
              <div className="text-center py-12 text-gray-600 dark:text-gray-300">
                <LightBulbIcon className="h-16 w-16 mx-auto mb-4 opacity-60" />
                <p>{t('financialQuestions:noQuestions', 'No questions yet')}</p>
                <p className="text-sm mt-2 text-gray-500 dark:text-gray-400">
                  {t('financialQuestions:getStarted', 'Ask a question to get started')}
                </p>
              </div>
            ) : (
              [...questionHistory].reverse().map((result, reversedIndex) => {
                // Calculate the actual index in the original array for operations
                const actualIndex = questionHistory.length - 1 - reversedIndex;
                return (
                <div
                  key={`${result.session_id}-${actualIndex}`}
                  className="bg-white dark:bg-gray-700 rounded-lg shadow p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <span className="inline-block px-2 py-1 text-xs font-medium bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-100 rounded">
                        {categories.find(c => c.id === result.category)?.name || result.category}
                      </span>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                        {result.timestamp.toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <ExportDropdownMenu
                        onExport={handleExportForResult(actualIndex)}
                        onPrint={handlePrintForResult(actualIndex)}
                        showPrint={true}
                      />
                      <button
                        onClick={() => deleteQuestion(actualIndex)}
                        className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                        title={t('financialQuestions:delete', 'Delete')}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      {t('financialQuestions:yourQuestion', 'Your Question')}:
                    </h4>
                    <p className="text-gray-700 dark:text-gray-200 text-sm italic">
                      {result.question}
                    </p>
                  </div>

                  <div className="prose dark:prose-invert max-w-none text-gray-900 dark:text-gray-100 prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-p:text-gray-800 dark:prose-p:text-gray-200 prose-li:text-gray-800 dark:prose-li:text-gray-200 prose-strong:text-gray-900 dark:prose-strong:text-white">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {result.answer}
                    </ReactMarkdown>
                  </div>

                  {result.key_points.length > 0 && (
                    <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
                        {t('financialQuestions:keyPoints', 'Key Points')}
                      </h4>
                      <ul className="list-disc list-inside space-y-1">
                        {result.key_points.map((point, i) => (
                          <li key={i} className="text-sm text-green-700 dark:text-green-300">
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.action_items.length > 0 && (
                    <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                        {t('financialQuestions:actionItems', 'Suggested Actions')}
                      </h4>
                      <ul className="list-disc list-inside space-y-1">
                        {result.action_items.map((item, i) => (
                          <li key={i} className="text-sm text-blue-700 dark:text-blue-300">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.follow_up_suggestions.length > 0 && (
                    <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <h4 className="font-medium text-purple-800 dark:text-purple-200 mb-2">
                        {t('financialQuestions:suggestedFollowUps', 'Related Questions')}
                      </h4>
                      <ul className="space-y-1">
                        {result.follow_up_suggestions.map((suggestion, i) => (
                          <li
                            key={i}
                            className="text-sm text-purple-700 dark:text-purple-300 cursor-pointer hover:underline"
                            onClick={() => setFollowUpQuestion(suggestion)}
                          >
                            {suggestion}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.additional_reading && result.additional_reading.length > 0 && (
                    <div className="mt-4 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                      <h4 className="font-medium text-indigo-800 dark:text-indigo-200 mb-2 flex items-center">
                        <LinkIcon className="h-5 w-5 mr-2" />
                        {t('financialQuestions:additionalReading', 'Learn More')}
                      </h4>
                      <ul className="space-y-2">
                        {result.additional_reading.map((link, i) => (
                          <li key={i} className="text-sm">
                            <a
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-700 dark:text-indigo-300 hover:underline font-medium"
                            >
                              {link.title}
                            </a>
                            <span className="text-indigo-600 dark:text-indigo-400 ml-2">
                              — {link.source}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
                    <p className="text-xs text-amber-800 dark:text-amber-200">
                      <ExclamationTriangleIcon className="h-4 w-4 inline mr-1" />
                      {result.disclaimers[0]}
                    </p>
                  </div>
                </div>
              );})
            )}
          </div>

          {/* Follow-up Input */}
          {activeSessionId && questionHistory.length > 0 && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={followUpQuestion}
                  onChange={(e) => setFollowUpQuestion(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleFollowUp(activeSessionId)}
                  placeholder={t('financialQuestions:form.followUpPlaceholder', 'Ask a follow-up question...')}
                  className="flex-1 px-4 py-2 bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-amber-500"
                  disabled={loading}
                />
                <button
                  onClick={() => handleFollowUp(activeSessionId)}
                  disabled={loading || !followUpQuestion.trim()}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? '...' : t('financialQuestions:form.askFollowUp', 'Ask')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Disclaimer Footer */}
      <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg max-w-7xl mx-auto w-full">
        <p className="text-sm text-amber-800 dark:text-amber-200 text-center">
          <strong>{t('financialQuestions:disclaimer.title', 'Disclaimer')}:</strong>{' '}
          {t('financialQuestions:disclaimer.text', 'This tool provides general financial information for educational purposes only. It is not financial advice. Please consult a qualified financial advisor for personalized guidance.')}
        </p>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
            <span className="text-gray-900 dark:text-gray-100">{t('financialQuestions:processing', 'Getting answer...')}</span>
          </div>
        </div>
      )}

    </div>
  );
};

export default FinancialQuestions;
